import _ from 'lodash';
import { Member } from './Member';
import { Membership } from './Membership';
import { Session } from './Session';
import { log } from '../log';
import { Model } from './Model';
import moment from 'moment';
import { getApp } from '../app';

export class Swipe extends Model {

  /**
   * @property id
   *
   * Unique Swipe ID.
   *
   * @type {string}
   */

  /**
   * @property stationId
   *
   * The id of the station that originated this event.
   *
   * @type {string}
   */

  /**
   * @property error
   *
   * Error message, if there was a problem with this event.
   *
   * @type {string}
   */

  /**
   * @property memberId
   *
   * The Member ID of the user that initiated this event.
   *
   * @type {string}
   */

  /**
   * @property membershipId
   *
   * The Membership ID that this event is associated to.
   *
   * @type {string}
   */

  /**
   * @property sessionId
   *
   * The Session ID that this event is associated to.
   *
   * @type {string}
   */
  get sessionId() { return this.session.id; }

  /**
   * @property session
   *
   * The Session that this event is associated to.
   *
   * @type {Session}
   */

  /**
   * @property member
   *
   * Member record.  Will be populated when this event is sent to
   * a browser, if the event has a `memberId`.
   *
   * @type {Member}
   */

  /**
   * @property card
   *
   * The card that was swiped.
   *
   * @type {string}
   */

  /**
   * @property valid
   *
   * Is this a good swipe for a valid card?
   *
   * @type {boolean}
   */

  constructor( options={} ) {
    const { session, ...opts } = options;
    super( opts );
    if ( session ) this.session = new Session( session );
  }

  /**
   * The member object for the member associated with this swipe.
   *
   * @type {Member}
   */
  get member() {
    return this.session && this.session.getMember( this.memberId );
  }

  /**
   * The membership object for the membership associated with this swipe.
   *
   * @type {Membership}
   */
  get membership() { return this.session && this.session.membership; }

  /**
   * Time that the event was collected by the reader.
   *
   * @type {moment}
   */
  get readerTime() { return moment( this._readerTime ); }
  set readerTime( time ) { this._readerTime = to_time( time ); }

  /**
   * Time that the event was emitted to the server from the reader.
   *
   * @type {moment}
   */
  get emitTime() { return moment( this._emitTime ); }
  set emitTime( time ) { this._emitTime = to_time( time ); }

  /**
   * Time that the event was processed and validated by the server.
   *
   * @type {moment}
   */
  get serverTime() { return moment( this._serverTime ); }
  set serverTime( time ) { this._serverTime = to_time( time ); }

  getDefaultAction() {
    if ( this.session ) {
      // There is an active session for this membership
      if ( this.session.isPresent( this.memberId ) ) {
        // TODO - If the swiping member is the only adult present, then
        // default to "sign everyone out"
        // The member that has swiped is part of that session
        return {
          event     : 'member-out',
          memberId  : this.memberId,
          label     : 'sign out',
        };
      } else {
        // The member that has swiped is not part of that session
        return {
          event     : 'member-out',
          memberId  : this.memberId,
          label     : 'sign in',
        };
      }
    } else {
      return {
        event     : 'member-in',
        memberId  : this.memberId,
        label     : 'sign in',
      };
    }
  }

  /*
  get ident() {
    // TODO - allow for other ident methods
    return _.pick( this, 'memberId', 'card' );
  }

  send( data ) {
    _.assign( data, {
      membershipId    : this.membershipId,
      sessionId       : this.id,
      ident           : this.ident,
    } );
    // log.debug( 'SENDING SWIPE RESPONSE', data );
    return this.constructor.sender( this.sessionId, data ).then( newdata => {
      this.session = new Session( newdata );
      this.emit( 'updated', this );
      return this.session;
    } );
  }

  static sender( id, data ) {
    return getApp().service( 'sessions' ).patch( id, data );
    / *
    const request = require( 'request-promise' );
    return request( {
      method    : 'POST',
      baseUrl   : 'http://localhost:4000/',
      uri       : `/swipes/${id}`,
      json      : true,
      simple    : true,
      body      : data,
    } ).then( res => this.hydrate( res ) );
    * /
  }
  */

}

function to_time( time ) {
  if ( _.isNil( time ) || _.isNumber( time ) ) return time;
  if ( moment.isMoment( time ) ) return time.unix();
  if ( _.isDate( time ) ) return ( time.getTime() / 1000 );
  throw new Error( `Invalid date/time "${time}"` );
}
